import type { SupportedLocale } from '@fleetcore/shared'
import { useEffect, useState } from 'react'

import { INTL_LOCALE } from '../../i18n/format'

/**
 * "Last seen" as a relative time, in FR and EN.
 *
 * `Intl.RelativeTimeFormat` rather than a hand-written table: French needs "il y a
 * 2 minutes" with the right plural and "il y a 1 heure" without an s, and every
 * language gets that wrong differently. The unit choice is ours because the
 * thresholds are a product decision — a fleet map that says "il y a 90 minutes"
 * instead of "il y a 2 heures" is technically right and useless at a glance.
 */

const MINUTE = 60
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export interface RelativeParts {
  value: number
  unit: Intl.RelativeTimeFormatUnit
}

export function relativeParts(ageSeconds: number): RelativeParts {
  const age = Math.max(0, Math.round(ageSeconds))
  if (age < MINUTE) return { value: -age, unit: 'second' }
  if (age < HOUR) return { value: -Math.round(age / MINUTE), unit: 'minute' }
  if (age < DAY) return { value: -Math.round(age / HOUR), unit: 'hour' }
  return { value: -Math.round(age / DAY), unit: 'day' }
}

const formatters = new Map<string, Intl.RelativeTimeFormat>()

function formatter(locale: SupportedLocale): Intl.RelativeTimeFormat {
  let instance = formatters.get(locale)
  if (instance === undefined) {
    instance = new Intl.RelativeTimeFormat(INTL_LOCALE[locale], { numeric: 'auto' })
    formatters.set(locale, instance)
  }
  return instance
}

/**
 * Formats an age. Under five seconds it reads as "maintenant" / "now" rather than
 * "il y a 0 seconde", which is what `numeric: 'auto'` produces and which reads as a
 * rounding artefact.
 */
export function formatAge(locale: SupportedLocale, ageSeconds: number): string {
  const { value, unit } = relativeParts(ageSeconds)
  return formatter(locale).format(value, unit)
}

/** Age in seconds against the supplied clock, or null when never seen. */
export function ageSecondsOf(iso: string | null, nowMs: number): number | null {
  if (iso === null) return null
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return null
  // Clamped: a buffered flush and a skewed device clock both produce a future stamp,
  // and "dans 3 minutes" beside a truck is nonsense a dispatcher will report as a bug.
  return Math.max(0, (nowMs - ms) / 1000)
}

/**
 * A ticking clock for relative labels.
 *
 * 15 s, not 1 s: the labels are minute-grained, so a per-second tick would re-render
 * the panel sixty times for fifty-nine identical strings. A map with a freshness
 * indicator that never advances is exactly the "looks live, is not" failure this
 * screen has to avoid, so the tick is not optional either.
 */
export function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(timer)
  }, [intervalMs])

  return now
}
